import Phaser from "phaser";
import {
  bossKey,
  pickTeammate,
  plantKey,
  TEAMMATE_LABEL,
  TEAMMATE_VISUALS,
  troopaKey,
  walkerKey,
  type TeammateId,
} from "../data/teammates";
import { TILE } from "../constants";
import { GRAVITY_FALL, SHELL_SPEED, TROOPA_SPEED, WALKER_SPEED } from "../systems/platformPhysics";

export type EnemyRole = "walker" | "troopa" | "shell" | "plant" | "podoboo" | "boss";

/** Native piranha sprite height; also how far it rises out of its pipe. */
export const PLANT_HEIGHT = 36;

export class Enemy extends Phaser.Physics.Arcade.Sprite {
  readonly portrait?: Phaser.GameObjects.Image;
  role: EnemyRole;
  teammate: TeammateId;
  dir = -1;
  color: "green" | "red" = "green";
  stomped = false;
  shellMoving = false;
  /** True once the player has kicked this shell; only then do chain hits score. */
  kickedByPlayer = false;
  /** Enemies hit during the current kick; resets when the shell stops. */
  chainHits = 0;
  /** The first kick awards points; repeated re-kicks of the same shell do not. */
  kickScored = false;
  reviveAt = 0;
  plantPhase: "down" | "up" | "wait" = "down";
  plantTimer = 0;
  homeX: number;
  homeY: number;
  hits = 0;
  walkTimer = 0;
  revealed = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    role: EnemyRole,
    options: { color?: "green" | "red"; teammate?: TeammateId } = {},
  ) {
    const teammate = options.teammate ?? pickTeammate();
    super(scene, x, y, textureFor(role, teammate, 1));
    this.role = role;
    this.teammate = teammate;
    this.color = options.color ?? "green";
    this.homeX = x;
    this.homeY = y;
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setOrigin(0.5, 1);
    // Plants render behind pipe tiles (depth 8) so they emerge from inside.
    this.setDepth(role === "plant" ? 6 : 18);
    if (role !== "podoboo" && role !== "plant") {
      this.portrait = scene.add
        .image(x, y - 36, TEAMMATE_VISUALS[teammate].portraitKey)
        .setDepth(19);
    }
    this.setupBody();
  }

  get arcade(): Phaser.Physics.Arcade.Body {
    return this.body as Phaser.Physics.Arcade.Body;
  }

  get label(): string {
    return TEAMMATE_LABEL[this.teammate];
  }

  setupBody(): void {
    const body = this.arcade;
    body.setAllowGravity(this.role !== "plant" && this.role !== "podoboo");
    body.setGravityY(this.role === "plant" || this.role === "podoboo" ? 0 : GRAVITY_FALL);
    body.setImmovable(this.role === "plant");
    if (this.role === "walker") this.setSize(24, 24).setOffset(16, 31);
    else if (this.role === "troopa") this.setSize(24, 38).setOffset(16, 34);
    else if (this.role === "shell") this.setSize(28, 24).setOffset(14, 20);
    else if (this.role === "plant") this.setSize(18, 30).setOffset(3, 4);
    else if (this.role === "podoboo") this.setSize(18, 24).setOffset(4, 8);
    else this.setSize(56, 72).setOffset(28, 40);
    // Plants are scripted: the body follows the sprite and never integrates.
    body.moves = this.role !== "plant";
    if (this.role === "plant") body.enable = false;
  }

  stomp(): "kill" | "shell" | "stop" | "bounce" {
    if (this.role === "plant" || this.role === "podoboo") return "bounce";
    if (this.role === "boss") return "bounce";
    if (this.role === "walker") {
      this.flatten();
      return "kill";
    }
    if (this.role === "troopa") {
      this.becomeShell(false);
      return "shell";
    }
    if (this.role === "shell") {
      if (this.shellMoving) {
        this.shellMoving = false;
        this.chainHits = 0;
        this.arcade.setVelocityX(0);
        this.reviveAt = this.scene.time.now + 4000;
        return "stop";
      }
      return "stop";
    }
    return "kill";
  }

  /** Kick the shell. Returns true when this kick should award kick points. */
  kick(dir: number): boolean {
    if (this.role !== "shell") this.becomeShell(true);
    this.shellMoving = true;
    this.kickedByPlayer = true;
    this.chainHits = 0;
    this.dir = dir >= 0 ? 1 : -1;
    this.arcade.setVelocityX(this.dir * SHELL_SPEED);
    this.reviveAt = 0;
    const award = !this.kickScored;
    this.kickScored = true;
    return award;
  }

  /** Register an enemy hit by this moving shell; returns the 1-based chain index or 0. */
  registerChainHit(): number {
    if (this.role !== "shell" || !this.shellMoving || !this.kickedByPlayer) return 0;
    this.chainHits += 1;
    return this.chainHits;
  }

  /** Snapshot for persistence across sub-areas. */
  get persistedState(): { role: "shell"; x: number } | undefined {
    if (this.role !== "shell" || this.stomped) return undefined;
    return { role: "shell", x: this.x };
  }

  /** Restore an intermediate state recorded by {@link persistedState}. */
  restoreState(state: { role: "shell"; x: number }): void {
    if (state.role === "shell" && this.role === "troopa") {
      this.x = state.x;
      this.becomeShell(false);
      this.arcade.updateFromGameObject();
    }
  }

  flatten(): void {
    this.stomped = true;
    this.arcade.setVelocity(0, 0);
    this.arcade.enable = false;
    this.setTexture(walkerKey(this.teammate, "flat"));
    this.portrait?.setScale(1, 0.34);
    this.scene.time.delayedCall(500, () => this.destroy());
  }

  becomeShell(moving: boolean): void {
    this.role = "shell";
    this.shellMoving = moving;
    this.setTexture(troopaKey(this.teammate, "shell"));
    this.setupBody();
    this.arcade.setVelocityX(moving ? this.dir * SHELL_SPEED : 0);
    this.reviveAt = moving ? 0 : this.scene.time.now + 4500;
  }

  hitByFire(): boolean {
    if (this.role === "boss") {
      this.hits += 1;
      this.setTint(0xffffff);
      this.portrait?.setTint(0xffffff);
      this.scene.time.delayedCall(80, () => {
        this.clearTint();
        this.portrait?.clearTint();
      });
      if (this.hits >= 5) {
        this.revealed = true;
        this.flattenAsWalker();
        return true;
      }
      return false;
    }
    this.flatten();
    return true;
  }

  flattenAsWalker(): void {
    this.role = "walker";
    this.flatten();
  }

  think(
    dt: number,
    playerX: number,
    solidAt: (x: number, y: number) => boolean,
  ): void {
    if (this.stomped || !this.active) return;
    this.walkTimer += dt;
    const frame: 1 | 2 = Math.floor(this.walkTimer * 6) % 2 === 0 ? 1 : 2;

    if (this.role === "walker" || this.role === "troopa") {
      this.arcade.setVelocityX(this.dir * (this.role === "walker" ? WALKER_SPEED : TROOPA_SPEED));
      this.setFlipX(this.dir > 0);
      if (this.arcade.blocked.left) this.dir = 1;
      if (this.arcade.blocked.right) this.dir = -1;
      if (this.role === "troopa" && this.color === "red" && this.arcade.blocked.down) {
        const probeX = this.x + this.dir * 8;
        const probeY = this.y + 2;
        if (!solidAt(probeX, probeY)) this.dir *= -1;
      }
      this.setTexture(
        this.role === "walker" ? walkerKey(this.teammate, frame) : troopaKey(this.teammate, frame),
      );
      return;
    }

    if (this.role === "shell") {
      if (this.shellMoving) {
        this.arcade.setVelocityX(this.dir * SHELL_SPEED);
        if (this.arcade.blocked.left) this.dir = 1;
        if (this.arcade.blocked.right) this.dir = -1;
      } else if (this.reviveAt && this.scene.time.now >= this.reviveAt) {
        this.role = "troopa";
        this.kickedByPlayer = false;
        this.kickScored = false;
        this.chainHits = 0;
        this.setupBody();
        this.setTexture(troopaKey(this.teammate, 1));
      }
      return;
    }

    if (this.role === "plant") {
      const body = this.arcade;
      const maxRise = PLANT_HEIGHT;
      this.x = this.homeX;
      body.setVelocity(0, 0);
      this.plantTimer += dt;
      const near = Math.abs(playerX - this.homeX) < TILE * 2.25;
      if (this.plantPhase === "down" && this.plantTimer > 1.4 && !near) {
        this.plantPhase = "up";
        this.plantTimer = 0;
      } else if (this.plantPhase === "up") {
        this.y = Math.max(this.homeY - maxRise, this.y - 62 * dt);
        if (this.y <= this.homeY - maxRise) {
          this.y = this.homeY - maxRise;
          this.plantPhase = "wait";
          this.plantTimer = 0;
        }
      } else if (this.plantPhase === "wait" && this.plantTimer > 1.1) {
        this.plantPhase = "down";
        this.plantTimer = 0;
      } else if (this.plantPhase === "down") {
        this.y = Math.min(this.homeY, this.y + 62 * dt);
      }
      this.setTexture(plantKey(this.teammate, frame));
      // Only bite once a meaningful part of the head is out of the pipe.
      body.enable = this.y < this.homeY - 10;
      body.updateFromGameObject();
      return;
    }

    if (this.role === "podoboo") {
      this.setTexture(
        Math.floor(this.walkTimer * 8) % 2 === 0
          ? "enemy-podoboo-1"
          : "enemy-podoboo-2",
      );
      if (!this.getData("launched")) {
        this.setData("launched", true);
        this.scene.time.addEvent({
          delay: 1600 + Math.random() * 800,
          loop: true,
          callback: () => {
            if (!this.active) return;
            this.y = this.homeY;
            this.arcade.setVelocityY(-420);
            this.arcade.setAllowGravity(true);
            this.arcade.setGravityY(GRAVITY_FALL);
          },
        });
      }
      if (this.y > this.homeY + TILE / 2) {
        this.y = this.homeY + TILE / 2;
        this.arcade.setVelocityY(0);
        this.arcade.setAllowGravity(false);
      }
      return;
    }

    if (this.role === "boss") {
      const toward = playerX < this.x ? -1 : 1;
      this.dir = toward;
      this.arcade.setVelocityX(toward * 42);
      this.setFlipX(toward > 0);
      this.setTexture(bossKey(this.teammate));
      if (this.arcade.blocked.down && Math.random() < 0.008) {
        this.arcade.setVelocityY(-330);
      }
    }
  }

  preUpdate(time: number, delta: number): void {
    super.preUpdate(time, delta);
    this.syncPortrait();
  }

  private syncPortrait(): void {
    const portrait = this.portrait;
    if (!portrait?.active) return;
    const visual = TEAMMATE_VISUALS[this.teammate];
    const bob = Math.sin(this.walkTimer * 12) * 1.2;
    // Heads sit on one-tile bodies like a goomba cap: big head, small feet.
    let size = 28 * visual.headScale;
    let yOffset = 34;
    if (this.role === "troopa") yOffset = 48;
    if (this.role === "shell") {
      size = 20 * visual.headScale;
      yOffset = 22;
    }
    if (this.role === "boss") {
      size = 56 * visual.headScale;
      yOffset = 74;
    }
    if (this.stomped) {
      size = 31 * visual.headScale;
      yOffset = 11;
    }
    portrait
      .setPosition(
        this.x + visual.offsetX,
        this.y - yOffset + visual.offsetY + (this.stomped ? 0 : bob),
      )
      .setDisplaySize(size, this.stomped ? Math.max(8, size * 0.3) : size)
      .setFlipX(this.flipX)
      .setAlpha(this.alpha)
      .setVisible(this.visible);
  }

  destroy(fromScene?: boolean): void {
    this.portrait?.destroy(fromScene);
    super.destroy(fromScene);
  }
}

function textureFor(role: EnemyRole, id: TeammateId, frame: 1 | 2): string {
  if (role === "walker") return walkerKey(id, frame);
  if (role === "troopa" || role === "shell") return role === "shell" ? troopaKey(id, "shell") : troopaKey(id, frame);
  if (role === "plant") return plantKey(id, frame);
  if (role === "boss") return bossKey(id);
  if (role === "podoboo") return "enemy-podoboo-1";
  return walkerKey(id, frame);
}
